import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

/* =============================================================
   CONTEÚDO DOS DOCUMENTOS
   ============================================================= */
const DOCS = {
  regulamento_clube: {
    title: "Regulamento do Clube",
    body: (
      <>
        <h4>Sobre o Clube Dive Master</h4>
        <p>
          O Clube Dive Master é uma assinatura anual que dá acesso a benefícios exclusivos,
          descontos em cursos, viagens, equipamentos e experiências da Dive Master, além
          da participação em sorteios previstos ao longo do ano.
        </p>

        <h4>Vigência</h4>
        <p>
          A assinatura tem duração de 12 meses a partir da confirmação do pagamento. Os
          benefícios são acumulativos e podem ser utilizados ao longo desse período.
        </p>

        <h4>Benefícios</h4>
        <ul>
          <li>Descontos exclusivos em cursos, expedições e equipamentos.</li>
          <li>Participação em sorteios do Clube.</li>
          <li>Acesso antecipado ao DiverTrack Premium.</li>
          <li>Novos benefícios liberados ao longo do ano.</li>
        </ul>

        <h4>Documentos complementares</h4>
        <p>
          Este regulamento deve ser lido em conjunto com as &quot;Regras dos Sorteios&quot;,
          &quot;Política de Cancelamento&quot;, &quot;Termos de Uso&quot; e &quot;Política de Privacidade&quot;.
        </p>

        <p className="opacity-70">
          Última atualização em 26/07/2027. A versão completa pode ser atualizada
          periodicamente pela Dive Master.
        </p>
      </>
    ),
  },
  regras_sorteios: {
    title: "Regras dos Sorteios",
    body: (
      <>
        <h4>Como funcionam os sorteios</h4>
        <p>
          Todos os sorteios do Clube Dive Master têm como base a{" "}
          <strong>Loteria Federal da Caixa Econômica Federal</strong>, garantindo
          transparência, imparcialidade e um processo público e auditável.
        </p>

        <h4>Bilhete numerado</h4>
        <p>
          Ao entrar no Clube, o associado recebe por e-mail seu(s) número(s) da sorte
          — a quantidade varia de acordo com o plano escolhido (10, 20 ou 40 números).
        </p>

        <h4>Apuração</h4>
        <p>
          Na data definida da premiação, utilizamos o resultado oficial da Loteria
          Federal. O primeiro prêmio (5 dígitos) é o número de referência.
        </p>
        <ul>
          <li>
            Vencedor: o associado que possuir o número da sorte igual ao 1º prêmio da
            Loteria Federal.
          </li>
          <li>
            Caso ninguém possua esse número: consideramos, na sequência, o 2º, 3º,
            4º e 5º prêmios até identificarmos um vencedor.
          </li>
          <li>
            Se nenhum dos 5 prêmios coincidir, vence quem tiver o número da sorte
            mais próximo <em>acima</em> do 1º prêmio.
          </li>
        </ul>

        <h4>Elegibilidade</h4>
        <p>
          Para participar de um sorteio, o associado precisa ter concluído sua compra
          e recebido seus números da sorte antes da data do respectivo sorteio.
        </p>

        <h4>Divulgação do vencedor</h4>
        <p>
          O nome do vencedor será divulgado nos canais oficiais da Dive Master
          (Instagram e site), sempre respeitando os limites da LGPD.
        </p>
      </>
    ),
  },
  cancelamento: {
    title: "Política de Cancelamento",
    body: (
      <>
        <p>
          O Clube Dive Master oferece uma garantia contratual de até{" "}
          <strong>14 dias</strong> após a confirmação da compra.
        </p>
        <p>
          Durante esse período, o associado poderá solicitar o cancelamento pelos canais
          oficiais da Dive Master.
        </p>

        <h4>Importante</h4>
        <p>
          O Clube Dive Master é uma <strong>assinatura anual</strong> com benefícios
          contínuos, e <strong>não a aquisição de um bilhete para um único sorteio</strong>.
        </p>
        <p>
          Ao longo da vigência, o associado tem acesso a descontos exclusivos, cashback,
          benefícios, campanhas especiais e participação nos sorteios previstos no
          regulamento.
        </p>

        <h4>Participação em Sorteios</h4>
        <p>
          Caso um sorteio aconteça durante o período de garantia e o associado esteja
          habilitado a participar, essa participação será considerada na análise de
          eventual solicitação de cancelamento.
        </p>
        <p>Cada solicitação será analisada individualmente, observando:</p>
        <ul>
          <li>data da compra;</li>
          <li>data da solicitação;</li>
          <li>benefícios utilizados;</li>
          <li>participação em sorteios;</li>
          <li>utilização de descontos;</li>
          <li>cashback;</li>
          <li>vouchers;</li>
          <li>acessos digitais;</li>
          <li>regulamento vigente;</li>
          <li>legislação aplicável.</li>
        </ul>

        <h4>Direito do Consumidor</h4>
        <p>
          Nas compras realizadas pela internet, serão respeitados todos os direitos
          previstos no <strong>Código de Defesa do Consumidor</strong>.
        </p>

        <h4>Solicitação</h4>
        <p>
          O pedido deverá ser realizado pelos canais oficiais da Dive Master contendo:
        </p>
        <ul>
          <li>Nome</li>
          <li>CPF</li>
          <li>E-mail</li>
          <li>Número do pedido</li>
        </ul>
        <p>
          Após o recebimento, nossa equipe realizará a análise e retornará com as
          orientações.
        </p>
      </>
    ),
  },
  termos: {
    title: "Termos de Uso",
    body: (
      <>
        <h4>Aceitação dos termos</h4>
        <p>
          Ao acessar este site, criar uma conta ou realizar uma compra, você declara ter
          lido, compreendido e concordado com estes Termos de Uso, com nossa Política de
          Privacidade e com os regulamentos aplicáveis.
        </p>

        <h4>Uso permitido</h4>
        <p>
          Você concorda em utilizar o site e os serviços da Dive Master apenas para
          finalidades lícitas, respeitando os demais usuários, as leis brasileiras e os
          termos aqui descritos.
        </p>

        <h4>Cadastro e conta</h4>
        <p>
          As informações fornecidas no cadastro devem ser verdadeiras, precisas e
          atualizadas. Você é responsável por manter a confidencialidade de eventuais
          credenciais de acesso.
        </p>

        <h4>Pagamentos</h4>
        <p>
          Os pagamentos são processados por parceiros certificados (InfinitePay,
          Stripe e/ou similares). A Dive Master não armazena dados sensíveis do cartão.
        </p>

        <h4>Propriedade intelectual</h4>
        <p>
          Todo o conteúdo do site — textos, imagens, marcas, logotipos, layout — é
          propriedade da Dive Master ou de seus parceiros e não pode ser reproduzido sem
          autorização.
        </p>

        <h4>Alterações</h4>
        <p>
          A Dive Master pode alterar estes termos a qualquer momento. As alterações
          entram em vigor a partir de sua publicação no site.
        </p>

        <h4>Foro</h4>
        <p>
          Fica eleito o foro da comarca de Porto Belo/SC para dirimir qualquer questão
          decorrente destes termos.
        </p>
      </>
    ),
  },
  privacidade: {
    title: "Política de Privacidade",
    body: (
      <>
        <h4>Compromisso com a sua privacidade</h4>
        <p>
          A Dive Master respeita sua privacidade e trata seus dados pessoais conforme a{" "}
          <strong>Lei Geral de Proteção de Dados (LGPD — Lei 13.709/2018)</strong>.
        </p>

        <h4>Dados coletados</h4>
        <ul>
          <li>Dados cadastrais: nome, e-mail, telefone, CPF (quando aplicável).</li>
          <li>Dados de navegação: cookies, IP, dispositivo e páginas visitadas.</li>
          <li>Dados de compra: histórico de pedidos, produtos e forma de pagamento.</li>
        </ul>

        <h4>Finalidades</h4>
        <p>
          Utilizamos seus dados para (i) processar compras e entregar benefícios, (ii)
          comunicar campanhas e novidades por canais oficiais, (iii) prevenir fraudes,
          (iv) cumprir obrigações legais e (v) melhorar a experiência do site.
        </p>

        <h4>Compartilhamento</h4>
        <p>
          Podemos compartilhar dados estritamente necessários com parceiros de pagamento,
          marketing e infraestrutura, sempre sob acordos de confidencialidade. Nunca
          vendemos seus dados.
        </p>

        <h4>Seus direitos</h4>
        <p>
          Você pode a qualquer momento solicitar acesso, correção, exclusão ou portabilidade
          dos seus dados pelos canais oficiais da Dive Master.
        </p>

        <h4>Retenção</h4>
        <p>
          Mantemos os dados apenas pelo tempo necessário às finalidades acima e ao
          cumprimento de obrigações legais.
        </p>

        <h4>Contato do Encarregado (DPO)</h4>
        <p>
          Para exercer seus direitos ou tirar dúvidas: <em>contato pelo WhatsApp
          oficial da Dive Master</em>.
        </p>
      </>
    ),
  },
};

/* =============================================================
   HOOK: Modal com History API (voltar do celular fecha o modal)
   ============================================================= */
function useHistoryModal(open, onClose) {
  const openedByPushRef = useRef(false);

  useEffect(() => {
    if (!open) return;

    // Push state so that the OS back button closes the modal instead of leaving
    try {
      window.history.pushState({ modalOpen: true }, "");
      openedByPushRef.current = true;
    } catch {
      // If pushState fails, ignore silently — modal still works normally
    }

    const onPop = () => {
      // User pressed back — close modal, don't leave page
      openedByPushRef.current = false;
      onClose?.();
    };

    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // If modal was closed by click/ESC/X (not by back), rewind the pushed state
      if (openedByPushRef.current) {
        openedByPushRef.current = false;
        try {
          window.history.back();
        } catch {
          /* noop */
        }
      }
    };
  }, [open, onClose]);
}

/* =============================================================
   MODAL COMPONENT
   ============================================================= */
function LegalModal({ open, docKey, onClose }) {
  const containerRef = useRef(null);
  const closeBtnRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useHistoryModal(open, onClose);

  // Lock body scroll + ESC + focus trap
  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key === "Tab" && containerRef.current) {
        // Basic focus trap
        const focusables = containerRef.current.querySelectorAll(
          'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);

    // Auto-focus close button
    const t = setTimeout(() => closeBtnRef.current?.focus(), 30);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
      // Restore focus to trigger element
      if (lastFocusedRef.current && lastFocusedRef.current.focus) {
        lastFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  const doc = docKey ? DOCS[docKey] : null;

  return (
    <AnimatePresence>
      {open && doc && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/80 px-3 py-6 backdrop-blur-md sm:px-6 sm:py-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: EASE }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-modal-title"
          data-testid="legal-modal"
        >
          <motion.div
            ref={containerRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-white/15 bg-[#071B2D] shadow-2xl shadow-black/70"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#071B2D]/95 px-5 py-4 backdrop-blur-md sm:px-8">
              <h2
                id="legal-modal-title"
                className="font-display text-base font-black uppercase tracking-tight text-white sm:text-lg"
              >
                {doc.title}
              </h2>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/80 transition-colors hover:border-white/30 hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
                data-testid="close-legal-modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="prose prose-sm max-w-none px-5 py-6 text-white/85 sm:px-8 sm:py-8 sm:prose-base">
              <style>{`
                .prose h4 { color: #7BE0D6; font-weight: 900; text-transform: uppercase; letter-spacing: 0.06em; font-size: 12px; margin-top: 1.4rem; margin-bottom: 0.5rem; }
                .prose p { color: rgba(255,255,255,0.85); font-size: 14px; line-height: 1.65; margin-bottom: 0.85rem; }
                .prose strong { color: #fff; font-weight: 700; }
                .prose ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 0.85rem; }
                .prose ul li { color: rgba(255,255,255,0.82); font-size: 14px; line-height: 1.55; margin-bottom: 0.25rem; }
                .prose em { color: rgba(255,255,255,0.75); font-style: italic; }
                @media (min-width: 640px) {
                  .prose p, .prose ul li { font-size: 15px; }
                }
              `}</style>
              {doc.body}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* =============================================================
   FOOTER — links discretos
   ============================================================= */
export default function LegalDocsFooter() {
  const [openKey, setOpenKey] = useState(null);

  const close = useCallback(() => setOpenKey(null), []);

  // Global event: any component can call:
  //   window.dispatchEvent(new CustomEvent('open-legal-doc', { detail: 'cancelamento' }))
  useEffect(() => {
    const onOpen = (e) => {
      const key = e?.detail;
      if (key && DOCS[key]) setOpenKey(key);
    };
    window.addEventListener("open-legal-doc", onOpen);
    return () => window.removeEventListener("open-legal-doc", onOpen);
  }, []);

  const links = [
    { key: "regulamento_clube", label: "Regulamento do Clube" },
    { key: "regras_sorteios", label: "Regras dos Sorteios" },
    { key: "cancelamento", label: "Política de Cancelamento" },
    { key: "termos", label: "Termos de Uso" },
    { key: "privacidade", label: "Política de Privacidade" },
  ];

  return (
    <>
      <div
        className="mx-auto max-w-4xl px-6 py-8 text-center"
        data-testid="legal-docs-footer"
      >
        <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-white/45 sm:text-xs">
          {links.map((l, i) => (
            <React.Fragment key={l.key}>
              <li>
                <button
                  type="button"
                  onClick={() => setOpenKey(l.key)}
                  className="underline-offset-4 transition-colors hover:text-white/85 hover:underline"
                  data-testid={`open-doc-${l.key}`}
                >
                  {l.label}
                </button>
              </li>
              {i < links.length - 1 && (
                <li className="text-white/20" aria-hidden="true">
                  ·
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </div>

      <LegalModal open={!!openKey} docKey={openKey} onClose={close} />
    </>
  );
}
